import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Loader2,
  Send,
  AlertCircle,
  Zap,
  Users,
  Workflow,
  ChevronUp,
  ChevronDown,
  UserPlus,
  ArrowLeft,
  CheckCircle2,
  Circle,
  AlertTriangle,
  FolderOpen,
  Search,
  Paperclip,
  Square,
  Mic,
  MicOff,
  Bot,
  Puzzle,
  Monitor,
  CalendarClock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PersonaSelector } from '../../components/PersonaSelector';
import { AiConfigDialog } from '../../components/AiConfigDialog';
import { TaskingBotChat as RootTaskingBotChat } from '../../components/TaskingBotChat';
import { ChatAttachmentsLibrary } from '../../components/ChatAttachmentsLibrary';
import { TaskingBotAvatarPortal } from '../../components/TaskingBotAvatarPortal';
import { UserAvatar } from '../../components/UserAvatar';
import { Image, Video, Music, FileText, Box, Download, ExternalLink, Pencil, Hammer, RefreshCw, Trash2, Mail, Shield, Clock, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { uploadChatAttachment } from '../../endpoints/chat-attachments/upload_POST.schema';
import { useAuth } from '../../helpers/useAuth';
import { useTranslation, useLocalization } from '../../helpers/useLocalization';
import { useAiModels } from '../../helpers/useAiModels';
import { useVoiceInput } from '../../helpers/useVoiceInput';

const MODEL_STORAGE_KEY = 'taskingbot-selected-model';
const AGENT_MODE_STORAGE_KEY = 'taskingbot-agent-mode';

function BrowserIcon({ size = 12 }: { size?: number }) {
  return (
    <svg viewBox="0 0 200 200" width={size} height={size} style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <rect x="20" y="40" width="160" height="120" rx="8" fill="none" stroke="currentColor" strokeWidth="10"/>
      <line x1="20" y1="70" x2="180" y2="70" stroke="currentColor" strokeWidth="10"/>
      <circle cx="40" cy="55" r="4" fill="#ef4444"/>
      <circle cx="55" cy="55" r="4" fill="#f59e0b"/>
      <circle cx="70" cy="55" r="4" fill="#10b981"/>
      <rect x="90" y="52" width="80" height="6" rx="3" fill="currentColor" opacity="0.3"/>
    </svg>
  );
}

type AgentMode = 'auto' | 'cloud_browser' | 'extension' | 'desktop';

const MEDIA_TOKEN_RE = /\[media:(image|video|audio|pdf|3d)\]\s+(https?:\/\/[^\s<>"')\]]+|\/[^\s<>"')\]]+|data:[^\s<>"')\]]+)/gi;

function extractMediaFromText(text: string): { type: string; url: string }[] {
  const results: { type: string; url: string }[] = [];
  let m: RegExpExecArray | null;
  const re = new RegExp(MEDIA_TOKEN_RE.source, MEDIA_TOKEN_RE.flags);
  while ((m = re.exec(text)) !== null) {
    results.push({ type: m[1].toLowerCase(), url: m[2].trim() });
  }
  return results;
}

function collectTaskAssets(
  taskResult: unknown,
  taskDescription: string | undefined,
  timelineEntries: any[],
  taskMessages: any[],
): { type: string; url: string; source: string }[] {
  const seen = new Set<string>();
  const assets: { type: string; url: string; source: string }[] = [];
  const add = (items: { type: string; url: string }[], source: string) => {
    for (const item of items) {
      if (!seen.has(item.url)) {
        seen.add(item.url);
        assets.push({ ...item, source });
      }
    }
  };

  // From task result
  if (taskResult) {
    const text = typeof taskResult === 'string' ? taskResult : JSON.stringify(taskResult);
    add(extractMediaFromText(text), 'Result');
  }

  // From timeline entries
  for (const entry of timelineEntries) {
    const entryText = [entry.description, entry.result]
      .filter(Boolean)
      .map((v) => (typeof v === 'string' ? v : JSON.stringify(v)))
      .join(' ');
    add(extractMediaFromText(entryText), entry.skillUsed || `Step ${entry.step}`);
  }

  // From chat messages
  for (const msg of taskMessages) {
    if (msg.content) {
      add(extractMediaFromText(msg.content), msg.role === 'assistant' ? 'TaskingBot' : 'You');
    }
  }

  return assets;
}

const MEDIA_ICON: Record<string, React.ReactNode> = {
  image: <Image className="w-4 h-4" />,
  video: <Video className="w-4 h-4" />,
  audio: <Music className="w-4 h-4" />,
  pdf: <FileText className="w-4 h-4" />,
  '3d': <Box className="w-4 h-4" />,
};
import { TaskDetailModal } from '../../components/TaskDetailModal';
import { useTaskingBot } from '../helpers/useTaskingBot';
import { useTaskingBotParticipants } from '../helpers/useTaskingBotParticipants';
import { useQuery, useQueryClient } from '../../lib/queryClient';
import { RichMessageContent } from '../../helpers/RichMessageContent';
import { MessageFeedback } from '../../components/MessageFeedback';
import superjson from 'superjson';

const HISTORY_KEY = 'taskingbot-message-history';
const MAX_HISTORY = 50;

type FocusedTaskTab = 'details' | 'chat' | 'assets' | 'team';

export type FocusTaskContext = {
  id: string;
  title: string;
  parentTaskId?: string | null;
  nonce: number;
  tab?: FocusedTaskTab;
};

type SearxResult = {
  title: string;
  url: string;
  description: string;
  image?: string | null;
  source?: string;
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderDetailText(value: unknown): string {
  const text =
    typeof value === 'string'
      ? value
      : value == null
        ? ''
        : JSON.stringify(value, null, 2);

  return text
    .replace(/\r\n?/g, '\n')
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '$1: $2')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1: $2')
    .replace(/`{1,3}([^`]+)`{1,3}/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*\n]+)\*/g, '$1')
    .replace(/^#{1,6}\s*/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '- ')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export type TaskHeaderInfo = {
  title: string;
  status: string;
  priority?: string;
  progress?: number;
};

export function TaskingBotChat({
  projectId: _projectId,
  focusTaskContext,
  onClearFocus,
  onTaskHeaderInfo,
  externalManageOpen,
  onExternalManageClose,
}: {
  projectId: string | null;
  focusTaskContext?: FocusTaskContext | null;
  onClearFocus?: () => void;
  onTaskHeaderInfo?: (info: TaskHeaderInfo) => void;
  externalManageOpen?: boolean;
  onExternalManageClose?: () => void;
}) {
  const { messages, isLoading, error, sendMessage, clearMessages, addSystemMessage, lastResponseRef } = useTaskingBot();
  const { activeParticipants } = useTaskingBotParticipants();
  const { authState } = useAuth();
  const { t } = useTranslation();
  const { language } = useLocalization();
  const queryClient = useQueryClient();
  const { data: modelsData } = useAiModels();
  const [input, setInput] = useState('');
  const [autoExecute, setAutoExecute] = useState(true);
  const [multiAgentMode, setMultiAgentMode] = useState<'off' | 'parallel' | 'sequential'>('off');
  const [selectedModel, setSelectedModel] = useState<string>(() => {
    try { return localStorage.getItem(MODEL_STORAGE_KEY) || 'glm-5:cloud'; } catch { return 'glm-5:cloud'; }
  });
  const [agentMode, setAgentMode] = useState<AgentMode>(() => {
    try { return (localStorage.getItem(AGENT_MODE_STORAGE_KEY) as AgentMode) || 'auto'; } catch { return 'auto'; }
  });
  const [sendingTaskChat, setSendingTaskChat] = useState(false);
  const [taskStreamText, setTaskStreamText] = useState('');
  const [taskChatStatusMessage, setTaskChatStatusMessage] = useState<string | null>(null);
  const [taskChatBackgroundThinking, setTaskChatBackgroundThinking] = useState(false);
  const [taskAttachmentLibraryOpen, setTaskAttachmentLibraryOpen] = useState(false);
  const [focusedTaskTab, setFocusedTaskTab] = useState<FocusedTaskTab>('details');
  const [manageTaskOpen, setManageTaskOpen] = useState(false);

  // Sync external manage open trigger from parent header
  useEffect(() => {
    if (externalManageOpen) setManageTaskOpen(true);
  }, [externalManageOpen]);
  const [editingPdfUrl, setEditingPdfUrl] = useState<string | null>(null);
  const [PdfEditorComp, setPdfEditorComp] = useState<React.ComponentType<any> | null>(null);
  const [regeneratingAsset, setRegeneratingAsset] = useState<string | null>(null); // URL of asset being regenerated

  // ── Team/Participants state ──
  const [teamParticipants, setTeamParticipants] = useState<any[]>([]);
  const [teamInvitations, setTeamInvitations] = useState<any[]>([]);
  const [isTaskOwner, setIsTaskOwner] = useState(false);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamFetched, setTeamFetched] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePermission, setInvitePermission] = useState<'view' | 'edit'>('edit');
  const [inviteExpiry, setInviteExpiry] = useState('7');
  const [inviteCreating, setInviteCreating] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [inviteCopied, setInviteCopied] = useState(false);

  // Lazy-load PdfEditor when user clicks edit on a PDF
  useEffect(() => {
    if (editingPdfUrl && !PdfEditorComp) {
      import('../../components/PdfEditor').then((mod) => setPdfEditorComp(() => mod.PdfEditor));
    }
  }, [editingPdfUrl, PdfEditorComp]);
  const [useSearxSearch, setUseSearxSearch] = useState(false);
  const { isListening, isSupported: isVoiceSupported, toggle: toggleVoice } = useVoiceInput({
    onResult: (transcript) => setInput((prev) => prev + transcript),
  });
  const handleVoiceToggle = useCallback(() => toggleVoice(), [toggleVoice]);

  // Persist model & agent mode
  useEffect(() => { try { localStorage.setItem(MODEL_STORAGE_KEY, selectedModel); } catch {} }, [selectedModel]);
  useEffect(() => { try { localStorage.setItem(AGENT_MODE_STORAGE_KEY, agentMode); } catch {} }, [agentMode]);

  const visibilityAbortBlockedRef = useRef(false);

  const availableModels = React.useMemo(() => {
    const cloud = modelsData?.cloudModels?.filter((m: any) => m.provider === 'ollama_cloud') || [];
    if (!cloud.find((m: any) => m.model === 'glm-5:cloud')) {
      cloud.unshift({ provider: 'ollama_cloud', model: 'glm-5:cloud', name: 'GLM-5 Cloud', tags: ['Default'] });
    }
    const local = modelsData?.localEnabled && modelsData.localModels
      ? modelsData.localModels.map((m: any) => ({ provider: 'ollama_local', model: m.model, name: `${m.name}${m.size ? ` (${m.size})` : ''} [Local]`, tags: ['Local'] }))
      : [];
    return [...cloud, ...local];
  }, [modelsData]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const taskChatRequestIdRef = useRef(0);
  const taskChatAbortRef = useRef<AbortController | null>(null);
  const pendingTaskAssistantCountRef = useRef(0);

  // Task-specific data when a task is focused
  const focusedTaskId = focusTaskContext?.id || null;

  // ── Connection resilience: prevent abort on tab switch / phone lock / rotation ──
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        visibilityAbortBlockedRef.current = true;
      } else {
        visibilityAbortBlockedRef.current = false;
        if (focusedTaskId) {
          queryClient.invalidateQueries({ queryKey: ['taskingbot-task-messages', focusedTaskId] });
          queryClient.invalidateQueries({ queryKey: ['taskingbot-task-detail', focusedTaskId] });
          queryClient.invalidateQueries({ queryKey: ['taskingbot-task-timeline', focusedTaskId] });
        }
        queryClient.invalidateQueries({ queryKey: ['taskingbot', 'tasks'] });
        queryClient.invalidateQueries({ queryKey: ['taskingbot', 'running-tasks'] });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('resize', () => {});
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [focusedTaskId, queryClient]);

  const fetchTaskJson = async (url: string) => {
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) return null;
    const text = await res.text();
    // Skip HTML responses (missing routes on older deployments)
    if (text.trim().toLowerCase().startsWith('<!doctype') || text.trim().toLowerCase().startsWith('<html')) return null;
    try { return superjson.parse<any>(text); } catch { try { return JSON.parse(text); } catch { return null; } }
  };

  const { data: taskDetail } = useQuery({
    queryKey: ['taskingbot-task-detail', focusedTaskId],
    queryFn: () => fetchTaskJson(`/_api/taskingbot/tasks/detail?taskId=${focusedTaskId}`),
    enabled: !!focusedTaskId,
  });

  // Push task header info to parent for top-bar rendering
  useEffect(() => {
    if (!onTaskHeaderInfo || !taskDetail) return;
    onTaskHeaderInfo({
      title: taskDetail.title || focusTaskContext?.title || 'Task',
      status: taskDetail.status || 'pending',
      priority: taskDetail.priority,
      progress: taskDetail.progress,
    });
  }, [taskDetail, onTaskHeaderInfo, focusTaskContext?.title]);

  const { data: taskTimeline } = useQuery({
    queryKey: ['taskingbot-task-timeline', focusedTaskId],
    queryFn: () => fetchTaskJson(`/_api/taskingbot/tasks/timeline?taskId=${focusedTaskId}`),
    enabled: !!focusedTaskId,
  });

  const { data: taskChatMessages, isLoading: taskChatLoading } = useQuery({
    queryKey: ['taskingbot-task-messages', focusedTaskId],
    queryFn: () => fetchTaskJson(`/_api/taskingbot/task-messages?taskId=${focusedTaskId}`),
    enabled: !!focusedTaskId,
    refetchInterval: 3000,
  });
  const taskAssistantCount = (taskChatMessages?.messages || []).filter(
    (m: any) => m?.role === 'assistant',
  ).length;
  const currentUser = authState.type === 'authenticated' ? authState.user : null;
  const taskAttachmentSessionId = focusedTaskId ? `taskingbot-task:${focusedTaskId}` : 'taskingbot-global';

  // Track previous active participants to detect joins
  const prevActiveParticipantsRef = useRef<string[]>([]);

  // Message history for up/down arrow recall
  const [messageHistory, setMessageHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const historyIndexRef = useRef(-1);
  const draftBeforeHistoryRef = useRef<string | null>(null);

  // Ctrl+R reverse search state
  const [reverseSearchActive, setReverseSearchActive] = useState(false);
  const [reverseSearchQuery, setReverseSearchQuery] = useState('');
  const [reverseSearchIndex, setReverseSearchIndex] = useState(0);
  const reverseSearchInputRef = useRef<HTMLInputElement>(null);
  const lastFocusNonceRef = useRef<number>(-1);

  // Detect participant joins and show notifications
  useEffect(() => {
    const prevIds = prevActiveParticipantsRef.current;
    const currentIds = activeParticipants.map((p) => p.id);

    // Find new participants (those in current but not in previous)
    const newParticipants = activeParticipants.filter((p) => !prevIds.includes(p.id));

    // Show join notifications for new participants
    for (const participant of newParticipants) {
      // Use the participant's icon for visual feedback
      const icon = participant.icon || '🤖';
      addSystemMessage(`${icon} ${participant.name} has joined the conversation`, participant.name);
    }

    // Update ref for next comparison
    prevActiveParticipantsRef.current = currentIds;
  }, [activeParticipants, addSystemMessage]);

  // Load message history from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(HISTORY_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setMessageHistory(parsed);
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    }
  }, [input]);

  // Auto-continuation: when autoExecute is on and the response indicates incomplete work,
  // automatically send a "continue" message to keep execution going.
  const autoContinueRef = useRef(false);
  useEffect(() => {
    if (isLoading || !autoExecute || !lastResponseRef.current || focusTaskContext) return;

    const response = lastResponseRef.current;
    lastResponseRef.current = null; // Consume — don't re-trigger

    // Detect if the response indicates incomplete work that should continue
    const incompletePatterns = [
      /\bpick up where I left off\b/i,
      /\bran out of time\b/i,
      /\bnot (?:yet )?(?:finished|completed|done)\b/i,
      /\bcontinue (?:with|from|the)\b/i,
      /\bmore (?:steps|work|items) remain/i,
      /\bpartially completed?\b/i,
      /\bstill (?:need|have) to\b/i,
      /\bremaining (?:steps|tasks|items)\b/i,
    ];

    const isIncomplete = incompletePatterns.some((re) => re.test(response));

    // Don't auto-continue error messages or very short responses
    const isError = response.startsWith('❌') || response.length < 20;

    if (isIncomplete && !isError && !autoContinueRef.current) {
      autoContinueRef.current = true;
      // Small delay to let the UI update, then auto-continue
      setTimeout(() => {
        autoContinueRef.current = false;
        sendMessage('continue');
      }, 1500);
    }
  }, [isLoading, autoExecute, lastResponseRef, focusTaskContext, sendMessage]);

  // When a task is focused, switch to requested tab or default to 'details'
  useEffect(() => {
    if (!focusTaskContext) return;
    if (lastFocusNonceRef.current === focusTaskContext.nonce) return;
    lastFocusNonceRef.current = focusTaskContext.nonce;

    setFocusedTaskTab(focusTaskContext.tab ?? 'details');
    setInput('');
    setTeamFetched(false);
    setTeamParticipants([]);
    setTeamInvitations([]);
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
    });
  }, [focusTaskContext]);

  // ── Team/Participants fetch & actions ──
  const fetchTeamParticipants = useCallback(async () => {
    if (!focusedTaskId) return;
    setTeamLoading(true);
    try {
      const res = await fetch('/_api/taskingbot/task-invite', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'list', taskId: focusedTaskId }),
      });
      if (res.ok) {
        const data = await res.json();
        setTeamParticipants(data.participants || []);
        setTeamInvitations(data.invitations || []);
        setIsTaskOwner(data.isOwner);
      }
    } catch (e) {
      console.error('[TaskingBotChat] fetchTeamParticipants error:', e);
    } finally {
      setTeamLoading(false);
      setTeamFetched(true);
    }
  }, [focusedTaskId]);

  // Auto-fetch team when Team tab is selected (only once per task)
  useEffect(() => {
    if (focusedTaskTab === 'team' && focusedTaskId && !teamFetched && !teamLoading) {
      fetchTeamParticipants();
    }
  }, [focusedTaskTab, focusedTaskId, teamFetched, teamLoading, fetchTeamParticipants]);

  const handleCreateInvite = useCallback(async () => {
    if (!focusedTaskId) return;
    setInviteCreating(true);
    try {
      const res = await fetch('/_api/taskingbot/task-invite', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create',
          taskId: focusedTaskId,
          email: inviteEmail || undefined,
          permissionLevel: invitePermission,
          expiresInDays: Number(inviteExpiry),
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setInviteUrl(data.inviteUrl || data.url);
        fetchTeamParticipants();
      } else {
        toast.error('Failed to create invite');
      }
    } catch (e) {
      toast.error('Error creating invite');
    } finally {
      setInviteCreating(false);
    }
  }, [focusedTaskId, inviteEmail, invitePermission, inviteExpiry, fetchTeamParticipants]);

  const handleRemoveParticipant = useCallback(async (userId: number) => {
    if (!focusedTaskId) return;
    try {
      const res = await fetch('/_api/taskingbot/task-invite', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'remove', taskId: focusedTaskId, userId }),
      });
      if (res.ok) {
        fetchTeamParticipants();
        toast.success('Participant removed');
      }
    } catch (e) {
      toast.error('Failed to remove participant');
    }
  }, [focusedTaskId, fetchTeamParticipants]);

  useEffect(() => {
    if (
      taskChatStatusMessage &&
      taskAssistantCount > pendingTaskAssistantCountRef.current
    ) {
      setTaskChatStatusMessage(null);
      setTaskChatBackgroundThinking(false);
    }
  }, [taskAssistantCount, taskChatStatusMessage]);

  const buildAttachmentInsertText = useCallback((attachment: {
    fileName: string;
    shareToken?: string | null;
    dataUrl?: string;
  }) => {
    const sharedUrl = attachment.shareToken
      ? `${window.location.origin}/_api/chat-attachments/shared?token=${attachment.shareToken}`
      : null;
    if (sharedUrl) return `[attachment:${attachment.fileName}] ${sharedUrl}`;
    if (attachment.dataUrl && attachment.dataUrl.length <= 5000) {
      return `[attachment:${attachment.fileName}] ${attachment.dataUrl}`;
    }
    return `[attachment:${attachment.fileName}]`;
  }, []);

  const appendTaskInput = useCallback((text: string) => {
    setInput((prev) => (prev.trim() ? `${prev}\n${text}` : text));
  }, []);

  const handleTaskAttachmentSelect = useCallback((selected: any) => {
    appendTaskInput(buildAttachmentInsertText(selected));
    setTaskAttachmentLibraryOpen(false);
  }, [appendTaskInput, buildAttachmentInsertText]);

  const handleTaskChatPaste = useCallback(async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items || !focusedTaskId) return;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.type.startsWith('image/')) continue;
      const file = item.getAsFile();
      if (!file) return;
      e.preventDefault();
      try {
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || ''));
          reader.onerror = () => reject(new Error('Failed to read pasted image'));
          reader.readAsDataURL(file);
        });
        const uploaded = await uploadChatAttachment({
          sessionId: taskAttachmentSessionId,
          fileName: file.name || 'pasted-image.png',
          fileType: file.type || 'image/png',
          fileSize: file.size,
          dataUrl,
        });
        appendTaskInput(buildAttachmentInsertText(uploaded.attachment));
      } catch (err) {
        console.error('[TaskChat] Failed to attach pasted image:', err);
      }
      return;
    }
  }, [appendTaskInput, buildAttachmentInsertText, focusedTaskId, taskAttachmentSessionId]);

  const handleRegenerateImage = useCallback(async (originalUrl: string, _source: string) => {
    setRegeneratingAsset(originalUrl);
    try {
      const res = await fetch('/_api/taskingbot/regenerate-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url: originalUrl, taskId: focusedTaskId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Failed' }));
        throw new Error(err.error || 'Image regeneration failed');
      }
      const data = await res.json();
      if (data.url) {
        toast.success('Image regenerated');
        // Refetch task data — the backend already replaced the old URL in task messages/results
        if (focusedTaskId) {
          queryClient.invalidateQueries({ queryKey: ['taskingbot-task-detail', focusedTaskId] });
          queryClient.invalidateQueries({ queryKey: ['taskingbot-task-messages', focusedTaskId] });
          queryClient.invalidateQueries({ queryKey: ['taskingbot-task-timeline', focusedTaskId] });
        }
      }
    } catch (err: any) {
      console.error('[Asset Regen]', err);
      toast.error(err.message || 'Image regeneration failed');
    } finally {
      setRegeneratingAsset(null);
    }
  }, [focusedTaskId, queryClient]);

  const sendTaskChatMessage = useCallback(async (messageOverride?: string) => {
    const message = (messageOverride ?? input).trim();
    if (!message || sendingTaskChat || !focusedTaskId) return;
    const requestId = Date.now();
    taskChatRequestIdRef.current = requestId;
    pendingTaskAssistantCountRef.current = taskAssistantCount;
    setSendingTaskChat(true);
    setTaskStreamText('');
    setTaskChatStatusMessage(null);
    setTaskChatBackgroundThinking(false);
    setInput('');
    let backgrounded = false;
    let sawVisibleChunk = false;
    const enterBackgroundMode = (statusMessage: string) => {
      if (taskChatRequestIdRef.current !== requestId || backgrounded || sawVisibleChunk) return;
      backgrounded = true;
      setSendingTaskChat(false);
      setTaskStreamText('');
      setTaskChatStatusMessage(statusMessage);
      setTaskChatBackgroundThinking(true);
      queryClient.invalidateQueries({ queryKey: ['taskingbot-task-messages', focusedTaskId] });
      queryClient.invalidateQueries({ queryKey: ['taskingbot-task-detail', focusedTaskId] });
    };
    const abortCtrl = new AbortController();
    taskChatAbortRef.current = abortCtrl;
    const backgroundTimer = window.setTimeout(() => {
      enterBackgroundMode(
        'TaskingBot is still working on this task. New replies will appear here automatically.',
      );
    }, 15000);
    try {
      const res = await fetch('/_api/taskingbot/task-messages', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          taskId: focusedTaskId,
          message,
          language: language !== 'en' ? language : undefined,
          model: selectedModel || undefined,
          channel: agentMode !== 'auto' ? agentMode : undefined,
        }),
        signal: abortCtrl.signal,
      });
      if (!res.ok || !res.body) {
        console.error('[TaskChat] POST failed:', res.status);
        window.clearTimeout(backgroundTimer);
        if (taskChatRequestIdRef.current === requestId) {
          setTaskChatStatusMessage('TaskingBot could not send this message. Please try again.');
          setTaskChatBackgroundThinking(false);
          setSendingTaskChat(false);
        }
        queryClient.invalidateQueries({ queryKey: ['taskingbot-task-messages', focusedTaskId] });
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        fullText += decoder.decode(value, { stream: true });
        const cleaned = fullText.replace(/\u200B/g, '');
        if (cleaned.trim()) {
          sawVisibleChunk = true;
          if (backgrounded) {
            setTaskChatStatusMessage(null);
            setTaskChatBackgroundThinking(false);
            setSendingTaskChat(true);
            backgrounded = false;
          }
          window.clearTimeout(backgroundTimer);
        }
        if (taskChatRequestIdRef.current === requestId) {
          setTaskStreamText(cleaned);
        }
      }
      window.clearTimeout(backgroundTimer);
      if (taskChatRequestIdRef.current === requestId) {
        setTaskStreamText('');
        setTaskChatStatusMessage(null);
        setTaskChatBackgroundThinking(false);
        setSendingTaskChat(false);
      }
      queryClient.invalidateQueries({ queryKey: ['taskingbot-task-messages', focusedTaskId] });
      queryClient.invalidateQueries({ queryKey: ['taskingbot-task-detail', focusedTaskId] });
    } catch (err: any) {
      window.clearTimeout(backgroundTimer);
      // If the tab is hidden (user switched away / phone locked), don't treat as error —
      // the backend continues processing via waitUntil. Just switch to background mode.
      if (err?.name === 'AbortError' && document.visibilityState === 'hidden') {
        if (taskChatRequestIdRef.current === requestId) {
          enterBackgroundMode('TaskingBot is still processing. Results will appear when you return.');
        }
      } else {
        console.error('[TaskChat] Error:', err);
        if (taskChatRequestIdRef.current === requestId) {
          setTaskChatStatusMessage(
            'TaskingBot could not stream the reply. We will keep refreshing in case it finishes in the background.',
          );
          setTaskChatBackgroundThinking(false);
          setSendingTaskChat(false);
        }
      }
      queryClient.invalidateQueries({ queryKey: ['taskingbot-task-messages', focusedTaskId] });
      queryClient.invalidateQueries({ queryKey: ['taskingbot-task-detail', focusedTaskId] });
    } finally {
      window.clearTimeout(backgroundTimer);
      taskChatAbortRef.current = null;
      if (taskChatRequestIdRef.current === requestId && !backgrounded) {
        setSendingTaskChat(false);
        setTaskStreamText('');
      }
    }
  }, [focusedTaskId, input, queryClient, sendingTaskChat, taskAssistantCount]);

  const stopTaskChatGeneration = useCallback(() => {
    if (taskChatAbortRef.current) {
      taskChatAbortRef.current.abort();
      taskChatAbortRef.current = null;
    }
    setSendingTaskChat(false);
    setTaskStreamText('');
    setTaskChatStatusMessage(null);
    setTaskChatBackgroundThinking(false);
  }, []);

  const saveHistory = (history: string[]) => {
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
    } catch {
      /* ignore */
    }
  };

  const navigateHistoryUp = useCallback(() => {
    if (messageHistory.length === 0) return;
    let newIndex = historyIndexRef.current;
    if (newIndex === -1) {
      draftBeforeHistoryRef.current = input;
    }
    if (newIndex < messageHistory.length - 1) {
      newIndex = newIndex + 1;
    } else if (newIndex === -1) {
      newIndex = 0;
    }
    historyIndexRef.current = newIndex;
    setHistoryIndex(newIndex);
    setInput(messageHistory[newIndex]);
  }, [messageHistory, input]);

  const navigateHistoryDown = useCallback(() => {
    if (messageHistory.length === 0) return;
    let newIndex = historyIndexRef.current;
    if (newIndex > 0) {
      newIndex = newIndex - 1;
      historyIndexRef.current = newIndex;
      setHistoryIndex(newIndex);
      setInput(messageHistory[newIndex]);
    } else if (newIndex === 0) {
      newIndex = -1;
      historyIndexRef.current = newIndex;
      setHistoryIndex(newIndex);
      setInput(draftBeforeHistoryRef.current ?? '');
      draftBeforeHistoryRef.current = null;
    }
  }, [messageHistory]);

  // Reverse search: find matches in history
  const reverseSearchMatches = React.useMemo(() => {
    if (!reverseSearchQuery.trim()) return [];
    const q = reverseSearchQuery.toLowerCase();
    return messageHistory.filter((m) => m.toLowerCase().includes(q));
  }, [messageHistory, reverseSearchQuery]);

  // When reverse search query or index changes, update the input preview
  useEffect(() => {
    if (!reverseSearchActive) return;
    if (reverseSearchMatches.length > 0 && reverseSearchIndex < reverseSearchMatches.length) {
      setInput(reverseSearchMatches[reverseSearchIndex]);
    }
  }, [reverseSearchActive, reverseSearchMatches, reverseSearchIndex]);

  const openReverseSearch = useCallback(() => {
    if (messageHistory.length === 0) return;
    draftBeforeHistoryRef.current = input;
    setReverseSearchActive(true);
    setReverseSearchQuery('');
    setReverseSearchIndex(0);
    setTimeout(() => reverseSearchInputRef.current?.focus(), 50);
  }, [messageHistory, input]);

  const closeReverseSearch = useCallback((restoreDraft = false) => {
    setReverseSearchActive(false);
    setReverseSearchQuery('');
    setReverseSearchIndex(0);
    if (restoreDraft && draftBeforeHistoryRef.current !== null) {
      setInput(draftBeforeHistoryRef.current);
      draftBeforeHistoryRef.current = null;
    }
    textareaRef.current?.focus();
  }, []);

  const handleReverseSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeReverseSearch(true);
    } else if (e.key === 'Enter') {
      // Accept the current match
      e.preventDefault();
      closeReverseSearch(false);
    } else if (e.key === 'r' && e.ctrlKey) {
      // Ctrl+R again cycles to next match
      e.preventDefault();
      if (reverseSearchMatches.length > 0) {
        setReverseSearchIndex((prev) =>
          prev + 1 < reverseSearchMatches.length ? prev + 1 : 0,
        );
      }
    }
  }, [closeReverseSearch, reverseSearchMatches]);

  const sendMessageText = useCallback(
    (text: string) => {
      sendMessage(text);
    },
    [sendMessage],
  );

  const fetchSearxResults = useCallback(async (query: string): Promise<SearxResult[]> => {
    const res = await fetch(`/_api/taskingbot/searx-search?q=${encodeURIComponent(query)}`, {
      credentials: 'include',
    });

    if (!res.ok) {
      throw new Error(`SearXNG search failed with status ${res.status}`);
    }

    const data = await res.json();
    return Array.isArray(data?.results) ? data.results : [];
  }, []);

  const formatSearxResultsMessage = useCallback((query: string, results: SearxResult[]) => {
    const cards = results
      .map((result) => {
        const safeTitle = escapeHtml(result.title || result.url);
        const safeUrl = escapeHtml(result.url);
        const safeSource = result.source ? escapeHtml(result.source) : '';
        const safeDescription = result.description ? escapeHtml(result.description) : '';
        const safeImage = result.image ? escapeHtml(result.image) : '';

        return `
<div style="margin: 0 0 16px 0; border: 1px solid rgba(255,255,255,0.12); border-radius: 16px; overflow: hidden; background: linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03));">
  ${safeImage ? `<img src="${safeImage}" alt="${safeTitle}" style="display:block; width:100%; max-height:220px; object-fit:cover; background:#0b0b0b;" />` : ''}
  <div style="padding: 14px 16px;">
    <div style="font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; opacity: 0.68; margin-bottom: 8px;">${safeSource || 'web result'}</div>
    <a href="${safeUrl}" target="_blank" rel="noopener noreferrer" style="display:block; font-size: 16px; font-weight: 700; line-height: 1.3; text-decoration: none; color: inherit; margin-bottom: 8px;">${safeTitle}</a>
    ${safeDescription ? `<p style="margin: 0; font-size: 14px; line-height: 1.5; opacity: 0.88;">${safeDescription}</p>` : ''}
  </div>
</div>`.trim();
      })
      .join('\n');

    return `### Search Results\n\n**Query:** ${escapeHtml(query)}\n\n${cards}`;
  }, []);

  const buildSearxPromptContext = useCallback((query: string, results: SearxResult[]) => {
    const lines = [
      '[SEARX_SEARCH_RESULTS]',
      `Query: ${query}`,
    ];

    for (const [index, result] of results.entries()) {
      lines.push(`${index + 1}. Title: ${result.title}`);
      lines.push(`   URL: ${result.url}`);
      if (result.source) {
        lines.push(`   Source: ${result.source}`);
      }
      if (result.description) {
        lines.push(`   Description: ${result.description}`);
      }
      if (result.image) {
        lines.push(`   Image: ${result.image}`);
      }
    }

    lines.push('[/SEARX_SEARCH_RESULTS]');
    lines.push('');
    lines.push('Use the search results above as current web context. Cite the sources naturally in your response when relevant.');
    lines.push(`User request: ${query}`);

    return lines.join('\n');
  }, []);

  const searxToggleButton = (
    <button
      type="button"
      onClick={() => setUseSearxSearch((prev) => !prev)}
      className={`inline-flex h-11 items-center gap-2 rounded-xl border px-3 py-2 text-xs font-bold uppercase tracking-[0.14em] transition-all duration-200 ${
        useSearxSearch
          ? 'border-primary/45 bg-primary/12 text-primary shadow-lg shadow-primary/10'
          : 'border-white/12 bg-white/5 text-foreground/60 hover:bg-white/10 hover:text-foreground'
      }`}
      title={useSearxSearch ? 'SearXNG search enabled' : 'Enable SearXNG search'}
      aria-label={useSearxSearch ? 'Disable SearXNG search' : 'Enable SearXNG search'}
      aria-pressed={useSearxSearch}
    >
      <Search className="h-3.5 w-3.5" />
      <span>{useSearxSearch ? 'Web Search On' : 'Web Search Off'}</span>
    </button>
  );

  const handleSend = useCallback(async () => {
    if (!input.trim()) return;
    // Don't block task chat sends on main chat loading state
    if (!focusedTaskId && isLoading) return;

    const trimmedInput = input.trim();
    let messageToSend = trimmedInput;
    let searchResultsMessage: string | undefined;

    if (!focusedTaskId && useSearxSearch && messageToSend && !messageToSend.startsWith('/')) {
      try {
        const searxResults = await fetchSearxResults(trimmedInput);
        if (searxResults.length > 0) {
          searchResultsMessage = formatSearxResultsMessage(trimmedInput, searxResults);
          messageToSend = buildSearxPromptContext(trimmedInput, searxResults);
        }
      } catch (err) {
        console.warn('[TaskingBotChat] SearXNG search failed, continuing without search context:', err);
      }
    }

    // Add to message history (avoid duplicates at top)
    const newHistory = [messageToSend, ...messageHistory.filter((m) => m !== messageToSend)].slice(
      0,
      MAX_HISTORY,
    );
    setMessageHistory(newHistory);
    saveHistory(newHistory);
    historyIndexRef.current = -1;
    setHistoryIndex(-1);
    draftBeforeHistoryRef.current = null;

    // If a task is focused, send via task-specific chat
    if (focusedTaskId) {
      await sendTaskChatMessage(messageToSend);
      return;
    }

    // Handle TaskingBot slash commands
    if (messageToSend.startsWith('/')) {
      const parts = messageToSend.split(' ');
      const command = parts[0];
      const args = parts.slice(1).join(' ');

      switch (command) {
        case '/plan':
          messageToSend = `/plan ${args || ''}`.trim();
          break;
        case '/parallel':
          setMultiAgentMode('parallel');
          setInput('');
          return;
        case '/sequential':
          setMultiAgentMode('sequential');
          setInput('');
          return;
        case '/auto':
          setAutoExecute(!autoExecute);
          setInput('');
          return;
        case '/skill':
          messageToSend = `/skill ${args}`;
          break;
        case '/execute':
          messageToSend = `Execute: ${args}`;
          break;
        default:
          break;
      }
    }

    await sendMessage(
      messageToSend,
      multiAgentMode !== 'off' ? multiAgentMode : activeParticipants.length > 0 ? 'parallel' : undefined,
      activeParticipants.length > 0 ? activeParticipants.map((p) => p.id) : undefined,
      searchResultsMessage,
    );
    setInput('');
  }, [
    input,
    isLoading,
    sendMessage,
    autoExecute,
    multiAgentMode,
    messageHistory,
    activeParticipants,
    useSearxSearch,
    fetchSearxResults,
    formatSearxResultsMessage,
    buildSearxPromptContext,
    focusedTaskId,
    sendTaskChatMessage,
  ]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
      return;
    }
    // Ctrl+R for reverse history search
    if (e.key === 'r' && e.ctrlKey) {
      e.preventDefault();
      openReverseSearch();
      return;
    }
    // Up/Down arrow for message history recall
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      if (messageHistory.length === 0) return;
      e.preventDefault();
      if (e.key === 'ArrowUp') {
        navigateHistoryUp();
      } else {
        navigateHistoryDown();
      }
    }
  };

  const getTaskStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-success" />;
      case 'running':
      case 'in_progress':
        return <Circle className="w-4 h-4 text-info animate-pulse" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-error" />;
      case 'waiting_for_input':
        return <AlertTriangle className="w-4 h-4 text-warning animate-pulse" />;
      default:
        return <Circle className="w-4 h-4 text-muted-foreground" />;
    }
  };

  // ── Task-focused chat view ──
  if (focusedTaskId) {
    const taskMessages = (taskChatMessages?.messages || []).filter((m: any) => {
      if (m.role !== 'user' && m.role !== 'assistant') return false;
      const c = String(m.content || '')
        .replace(/\[TOOL_START[^\]]*\][\s\S]*?\[\/TOOL_START\]/gs, '')
        .replace(/\[\/TOOL_END\]/g, '')
        .replace(/\[TOOL_START\][^\n]*/g, '')
        .replace(/\[ACTION_REPORT\][\s\S]*?\[\/ACTION_REPORT\]/g, '')
        .replace(/\[ACTIONS\][\s\S]*?\[\/ACTIONS\]/gi, '')
        .replace(/\[ACTION_RESULTS\][\s\S]*?\[\/ACTION_RESULTS\]/gi, '')
        .replace(/\[EXTENSION_ACTIONS:[^\]]*\]/g, '')
        .replace(/\[GROUND_TRUTH[^\]]*\][\s\S]*?\[\/GROUND_TRUTH[^\]]*\]/g, '')
        .replace(/\{\"actions\"\s*:\s*\[[\s\S]*?\]\s*\}/gi, '')
        .replace(/[\s\n\r\t\u200B\u200C\u200D\uFEFF]/g, '');
      return c.length > 0;
    });
    const timelineEntries = taskTimeline?.timeline || [];
    const taskTitle = taskDetail?.title || focusTaskContext?.title || 'Task';
    const taskStatus = taskDetail?.status || 'pending';
    const taskPriority = taskDetail?.priority;
    const taskProgress = taskDetail?.progress;
    const taskResult = taskDetail?.result;
    const taskDescription = taskDetail?.description;
    const isSending = sendingTaskChat;
    const hasExecutionHistory = timelineEntries.length > 0 || !!taskResult;
    const hasChatMessages = taskMessages.length > 0;
    const isEmpty = !hasExecutionHistory && !hasChatMessages && !taskChatLoading;

    return (
      <>
        <div className="h-full w-full flex flex-col bg-background">
        {/* DETAILS / CHAT / ASSETS tabs */}
        <div className="border-b border-border/30 bg-background/70 px-4 md:px-6">
          <div className="flex items-center gap-2">
            {([
              { id: 'details', label: t.taskingbot.details },
              { id: 'chat', label: t.taskingbot.chat },
              { id: 'assets', label: t.taskingbot.assets },
              { id: 'team', label: t.taskingbot.team || 'Team' },
            ] as const).map((tab) => {
              const isActive = focusedTaskTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setFocusedTaskTab(tab.id)}
                  title={tab.label}
                  aria-label={tab.label}
                  className={`border-b-2 px-1 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                    isActive
                      ? 'border-primary text-primary'
                      : 'border-transparent text-foreground/45 hover:text-foreground/70'
                  }`}
                >
                  {tab.id === 'assets' ? <FolderOpen className="mr-1 inline h-3.5 w-3.5" /> : null}
                  {tab.id === 'team' ? <Users className="mr-1 inline h-3.5 w-3.5" /> : null}
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Task Content — Chat tab uses root TaskingBotChat component for full UI */}
        {focusedTaskTab === 'chat' && focusedTaskId ? (
          <div className="flex-1 min-h-0 overflow-hidden">
            <RootTaskingBotChat key={`task-${focusedTaskId}`} projectId={null} taskId={focusedTaskId} />
          </div>
        ) : focusedTaskTab === 'chat' ? (
          <div className="flex-1 min-h-0 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
          </div>
        ) : (
        <div
          ref={scrollRef}
          className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6 py-4 space-y-4 scroll-smooth"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--scrollbar-thumb) var(--scrollbar-track)',
          }}
        >
          {taskChatLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex items-center gap-2 text-foreground/50">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                <span className="text-sm">{t.taskingbot.loadingHistory}</span>
              </div>
            </div>
          ) : focusedTaskTab === 'details' ? (
            !hasExecutionHistory && !taskDescription ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center space-y-4 max-w-sm">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 text-primary mx-auto">
                    <Zap className="w-7 h-7" strokeWidth={1.5} />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-foreground">{t.taskingbot.noTaskDetails}</p>
                    <p className="text-xs text-foreground/55 leading-relaxed">
                      {t.taskingbot.executionHistory}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {taskDescription && (
                  <div className="flex gap-3 justify-start animate-in fade-in-50 duration-200">
                    <div className="w-full bg-gradient-to-br from-primary/8 to-primary/4 border border-primary/20 text-foreground rounded-2xl rounded-bl-sm shadow-lg backdrop-blur-xl px-5 py-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-3.5 h-3.5 text-primary" />
                        <span className="text-[10px] font-bold text-primary uppercase tracking-wider">{t.taskingbot.taskCreated}</span>
                      </div>
                      <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{taskDescription}</p>
                    </div>
                  </div>
                )}

                {timelineEntries.map((entry: any, idx: number) => (
                  <div key={`tl-${idx}`} className="flex gap-3 justify-start animate-in fade-in-50 duration-200">
                    <div className={`w-full rounded-2xl rounded-bl-sm px-5 py-4 shadow-lg backdrop-blur-xl ${
                      entry.status === 'completed' ? 'bg-gradient-to-br from-success/8 to-success/4 border border-success/20' :
                      entry.status === 'failed' ? 'bg-gradient-to-br from-error/8 to-error/4 border border-error/20' :
                      entry.status === 'running' ? 'bg-gradient-to-br from-info/8 to-info/4 border border-info/20' :
                      'bg-gradient-to-br from-white/6 to-white/2 border border-white/12'
                    }`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        {entry.status === 'completed' ? <CheckCircle2 className="w-3.5 h-3.5 text-success" /> :
                         entry.status === 'failed' ? <AlertCircle className="w-3.5 h-3.5 text-error" /> :
                         entry.status === 'running' ? <Circle className="w-3.5 h-3.5 text-info animate-pulse" /> :
                         <Circle className="w-3.5 h-3.5 text-foreground/40" />}
                        <span className="text-[10px] font-bold uppercase tracking-wider text-foreground/60">
                          {t.taskingbot.step} {entry.step}{entry.skillUsed ? ` — ${entry.skillUsed}` : ''}
                        </span>
                        {entry.timestamp && (
                          <span className="text-[10px] text-foreground/30 ml-auto">
                            {new Date(entry.timestamp).toLocaleTimeString()}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-foreground leading-relaxed">{entry.description}</p>
                      {entry.result && (
                        <div className="mt-2 pt-2 border-t border-current/10 text-sm whitespace-pre-wrap break-words text-foreground/85">
                          {renderDetailText(entry.result)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {taskResult && (
                  <div className="flex gap-3 justify-start animate-in fade-in-50 duration-200">
                    <div className="w-full bg-gradient-to-br from-success/8 to-success/4 border border-success/20 text-foreground rounded-2xl rounded-bl-sm shadow-lg backdrop-blur-xl px-5 py-4">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-success" />
                        <span className="text-[10px] font-bold text-success uppercase tracking-wider">{t.taskingbot.result}</span>
                      </div>
                      <div className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                        {renderDetailText(taskResult)}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )
          ) : focusedTaskTab === 'assets' ? (
            (() => {
              const assets = collectTaskAssets(taskResult, taskDescription, timelineEntries, taskMessages);
              return assets.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center space-y-4 max-w-sm">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 text-primary mx-auto">
                      <FolderOpen className="w-7 h-7" strokeWidth={1.5} />
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-foreground">{t.taskingbot.noAssetsYet}</p>
                      <p className="text-xs text-foreground/55 leading-relaxed">
                        {t.taskingbot.assetsDescription}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-foreground/50">
                    {assets.length} asset{assets.length !== 1 ? 's' : ''}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {assets.map((asset, i) => (
                      <div
                        key={`${asset.url}-${i}`}
                        className="group relative overflow-hidden rounded-xl border border-white/10 bg-gradient-to-br from-white/6 to-white/2 backdrop-blur-xl transition-all hover:border-primary/30 hover:shadow-lg"
                      >
                        {asset.type === 'image' ? (
                          <div className="aspect-square overflow-hidden">
                            <img
                              src={asset.url}
                              alt={`Asset from ${asset.source}`}
                              className="h-full w-full object-cover transition-transform group-hover:scale-105"
                              loading="lazy"
                            />
                          </div>
                        ) : asset.type === 'video' ? (
                          <div className="aspect-video overflow-hidden bg-black/30">
                            <video
                              src={asset.url}
                              className="h-full w-full object-cover"
                              controls
                              preload="metadata"
                            />
                          </div>
                        ) : asset.type === 'audio' ? (
                          <div className="flex items-center justify-center p-6">
                            <audio src={asset.url} controls className="w-full" preload="metadata" />
                          </div>
                        ) : asset.type === 'pdf' ? (
                          <div className="aspect-[0.707] overflow-hidden bg-white/5">
                            <img src="https://tasking.tech/assets/taskingbot-pdf-cover.png" alt="PDF document" className="w-full h-full object-cover" />
                          </div>
                        ) : (
                          <div className="flex aspect-square items-center justify-center bg-white/3">
                            <div className="text-center space-y-2">
                              <div className="text-primary/60">{MEDIA_ICON[asset.type] || <FileText className="w-8 h-8" />}</div>
                              <p className="text-[10px] uppercase tracking-wider text-foreground/40">{asset.type}</p>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center justify-between gap-2 px-3 py-2 bg-black/20">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="text-primary/70">{MEDIA_ICON[asset.type]}</span>
                            <span className="text-[10px] font-medium text-foreground/50 truncate">{asset.source}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            {asset.type === 'image' && (
                              <button
                                onClick={() => handleRegenerateImage(asset.url, asset.source)}
                                disabled={regeneratingAsset === asset.url}
                                className="p-1 rounded-md hover:bg-white/10 text-foreground/40 hover:text-foreground/80 transition-colors disabled:opacity-40"
                                title="Regenerate image"
                              >
                                <RefreshCw className={`w-3 h-3 ${regeneratingAsset === asset.url ? 'animate-spin' : ''}`} />
                              </button>
                            )}
                            {asset.type === 'pdf' && (
                              <button
                                onClick={() => setEditingPdfUrl(asset.url)}
                                className="p-1 rounded-md hover:bg-white/10 text-foreground/40 hover:text-foreground/80 transition-colors"
                                title="Edit PDF"
                              >
                                <Pencil className="w-3 h-3" />
                              </button>
                            )}
                            <a
                              href={asset.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1 rounded-md hover:bg-white/10 text-foreground/40 hover:text-foreground/80 transition-colors"
                              title="Open in new tab"
                            >
                              <ExternalLink className="w-3 h-3" />
                            </a>
                            <a
                              href={asset.url}
                              download
                              className="p-1 rounded-md hover:bg-white/10 text-foreground/40 hover:text-foreground/80 transition-colors"
                              title="Download"
                            >
                              <Download className="w-3 h-3" />
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()
          ) : focusedTaskTab === 'team' ? (
            <div className="space-y-4">
              {/* Invite button */}
              {isTaskOwner && !showInviteForm && !inviteUrl && (
                <button
                  onClick={() => setShowInviteForm(true)}
                  className="flex items-center gap-2 w-full px-4 py-3 rounded-xl border border-dashed border-white/20 bg-transparent text-primary cursor-pointer text-sm font-semibold hover:bg-white/5 transition-all"
                >
                  <UserPlus size={16} />
                  Invite Participant
                </button>
              )}

              {/* Invite form */}
              {showInviteForm && !inviteUrl && (
                <div className="p-4 rounded-xl border border-white/15 bg-white/5 space-y-3">
                  <div className="flex items-center gap-2">
                    <Mail size={14} className="text-foreground/50" />
                    <input
                      type="email"
                      placeholder="Email (optional — restrict to this email)"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="flex-1 px-3 py-2 rounded-lg border border-white/15 bg-white/5 text-sm text-foreground placeholder:text-foreground/40"
                    />
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1 flex items-center gap-1.5">
                      <Shield size={14} className="text-foreground/50 flex-shrink-0" />
                      <select
                        value={invitePermission}
                        onChange={(e) => setInvitePermission(e.target.value as 'view' | 'edit')}
                        className="flex-1 px-2 py-2 rounded-lg border border-white/15 bg-white/5 text-sm text-foreground"
                      >
                        <option value="view">View Only</option>
                        <option value="edit">Can Edit</option>
                      </select>
                    </div>
                    <div className="flex-1 flex items-center gap-1.5">
                      <Clock size={14} className="text-foreground/50 flex-shrink-0" />
                      <select
                        value={inviteExpiry}
                        onChange={(e) => setInviteExpiry(e.target.value)}
                        className="flex-1 px-2 py-2 rounded-lg border border-white/15 bg-white/5 text-sm text-foreground"
                      >
                        <option value="1">1 Day</option>
                        <option value="7">7 Days</option>
                        <option value="30">30 Days</option>
                        <option value="90">90 Days</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="ghost" size="sm" onClick={() => { setShowInviteForm(false); setInviteEmail(''); }}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleCreateInvite} disabled={inviteCreating}>
                      {inviteCreating ? <><Loader2 size={14} className="animate-spin" /> Generating...</> : 'Generate Invite Link'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Invite success */}
              {inviteUrl && (
                <div className="p-4 rounded-xl border border-primary/30 bg-primary/5 space-y-3 text-center">
                  <div className="flex items-center justify-center gap-1.5 text-primary font-semibold text-sm">
                    <Check size={16} />
                    Invitation Created
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={inviteUrl}
                      className="flex-1 px-3 py-2 rounded-lg border border-white/15 bg-white/5 text-xs text-foreground"
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(inviteUrl);
                        setInviteCopied(true);
                        setTimeout(() => setInviteCopied(false), 2000);
                      }}
                    >
                      {inviteCopied ? <Check size={14} /> : <Copy size={14} />}
                    </Button>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => { setInviteUrl(null); setShowInviteForm(false); setInviteEmail(''); }}>
                    Done
                  </Button>
                </div>
              )}

              {/* Owner row */}
              {currentUser && (
                <div className="flex items-center gap-3 px-3 py-3 rounded-xl bg-primary/5 border border-primary/20">
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold text-xs flex-shrink-0">
                    {currentUser.displayName?.[0]?.toUpperCase() || 'O'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{currentUser.displayName || 'Owner'}</div>
                    <div className="text-[11px] text-foreground/50">{currentUser.email || ''}</div>
                  </div>
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-primary text-white uppercase tracking-wider">
                    Owner
                  </span>
                </div>
              )}

              {/* Participants list */}
              {teamLoading ? (
                <div className="flex items-center justify-center py-8 text-foreground/50">
                  <Loader2 size={20} className="animate-spin" />
                </div>
              ) : teamParticipants.length === 0 && teamInvitations.length === 0 ? (
                <div className="text-center py-8 text-foreground/50 text-sm">
                  <Users size={28} className="opacity-30 mx-auto mb-2" />
                  <p>No participants yet</p>
                  <p className="text-xs opacity-60 mt-1">Invite team members to collaborate on this task</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {teamParticipants.map((p: any) => (
                    <div key={p.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-white/10 bg-white/3">
                      <div className="w-8 h-8 rounded-full overflow-hidden bg-white/10 flex items-center justify-center font-semibold text-xs text-foreground/60 flex-shrink-0">
                        {p.avatarUrl ? (
                          <img src={p.avatarUrl} alt="" className="w-full h-full object-cover" />
                        ) : (
                          (p.displayName || p.email || '?')[0].toUpperCase()
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{p.displayName || p.email || 'User'}</div>
                        {p.email && <div className="text-[11px] text-foreground/50">{p.email}</div>}
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                        p.permissionLevel === 'edit'
                          ? 'bg-blue-500/15 text-blue-400'
                          : 'bg-white/10 text-foreground/50'
                      }`}>
                        {p.permissionLevel}
                      </span>
                      {isTaskOwner && (
                        <button
                          onClick={() => handleRemoveParticipant(p.userId)}
                          title="Remove participant"
                          className="p-1 rounded text-red-500 hover:bg-red-500/10 transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                  {teamInvitations.map((inv: any) => (
                    <div key={inv.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-dashed border-white/10 bg-white/2 opacity-60">
                      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                        <Mail size={14} className="text-foreground/40" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{inv.email || 'Invite link'}</div>
                        <div className="text-[11px] text-foreground/40">Pending invitation</div>
                      </div>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-yellow-500/15 text-yellow-400">
                        Pending
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : isEmpty ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center space-y-4 max-w-sm">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 text-primary mx-auto">
                  <Zap className="w-7 h-7" strokeWidth={1.5} />
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-foreground">{t.taskingbot.chatWithTaskingBot}</p>
                  <p className="text-xs text-foreground/55 leading-relaxed">
                    {t.taskingbot.askQuestions}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              {taskMessages.map((msg: any) => (
                <div
                  key={msg.id || `${msg.role}-${msg.createdAt}`}
                  className={`flex flex-row flex-nowrap gap-3 animate-in fade-in-50 duration-200 ${
                    msg.role === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  {msg.role !== 'user' && (
                    <div className="flex-shrink-0 pt-1">
                      <TaskingBotAvatarPortal size="sm" />
                    </div>
                  )}
                  <div
                    className={`rounded-2xl px-5 py-4 text-sm leading-relaxed transition-all min-w-0 overflow-hidden ${
                      msg.role === 'user'
                        ? 'max-w-[90%] bg-black text-white shadow-xl rounded-br-sm'
                        : 'max-w-full bg-gradient-to-br from-white/6 to-white/2 border border-white/12 text-foreground rounded-bl-sm shadow-xl backdrop-blur-xl'
                    }`}
                  >
                    {msg.role === 'user' ? (
                      <p className="whitespace-pre-wrap break-words font-medium tracking-tight">
                        {msg.content}
                      </p>
                    ) : (
                      <>
                        <RichMessageContent content={msg.content} onRegenerate={sendMessageText} />
                        <MessageFeedback
                          messageId={String(msg.id)}
                          source="taskingbot-task"
                          sourceId={focusedTaskId || undefined}
                          messageContent={msg.content}
                          compact
                        />
                      </>
                    )}
                    {msg.createdAt && (
                      <p className={`text-[10px] mt-2 ${msg.role === 'user' ? 'text-primary-foreground/60' : 'text-foreground/40'}`}>
                        {new Date(msg.createdAt).toLocaleTimeString()}
                      </p>
                    )}
                  </div>
                  {msg.role === 'user' && (
                    <div className="flex-shrink-0 pt-1">
                      <UserAvatar
                        user={{
                          displayName: currentUser?.displayName ?? 'You',
                          avatarUrl: currentUser?.avatarUrl ?? null,
                        }}
                        size="sm"
                        showStatus={true}
                      />
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
          {taskStreamText && (
            <div className="flex gap-3 animate-in fade-in-50 duration-200">
              <div className="flex-shrink-0 pt-1">
                <TaskingBotAvatarPortal size="sm" />
              </div>
              <div className="bg-gradient-to-br from-white/6 to-white/2 border border-white/12 rounded-2xl px-5 py-4 shadow-xl backdrop-blur-xl rounded-bl-sm max-w-full min-w-0 overflow-hidden">
                <RichMessageContent content={taskStreamText} onRegenerate={sendMessageText} />
              </div>
            </div>
          )}
          {taskChatStatusMessage && !taskStreamText && (
            <div className="flex gap-3 animate-in fade-in-50 duration-200">
              <div className="flex-shrink-0 pt-1">
                <TaskingBotAvatarPortal size="sm" />
              </div>
              <div className={`rounded-2xl px-5 py-4 shadow-xl backdrop-blur-xl rounded-bl-sm max-w-full min-w-0 overflow-hidden ${
                taskChatBackgroundThinking
                  ? 'bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20'
                  : 'bg-gradient-to-br from-info/8 to-info/4 border border-info/20'
              }`}>
                {taskChatBackgroundThinking && (
                  <div className="mb-2 flex items-center gap-2 text-primary/85">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-xs font-semibold uppercase tracking-[0.18em]">{t.taskingbot.taskingBotIsThinking}</span>
                  </div>
                )}
                <p className="text-sm leading-relaxed text-foreground/85">{taskChatStatusMessage}</p>
              </div>
            </div>
          )}
          {isSending && !taskStreamText && (
            <div className="flex gap-3 animate-in fade-in-50 duration-200">
              <div className="flex-shrink-0 pt-1">
                <TaskingBotAvatarPortal size="sm" />
              </div>
              <div className="bg-gradient-to-br from-white/6 to-white/2 border border-white/12 rounded-2xl px-5 py-4 shadow-xl backdrop-blur-xl rounded-bl-sm flex items-center gap-3">
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
                <span className="text-sm text-foreground/75 font-semibold">{t.taskingbot.sending}</span>
              </div>
            </div>
          )}
        </div>
        )}
        {/* Task input area for details/messages/assets tabs (chat tab has its own via RootTaskingBotChat) */}
        {focusedTaskTab !== 'chat' && (
          <>
          {/* Ctrl+R reverse search bar (task view) */}
          {reverseSearchActive && (
            <div className="mx-4 md:mx-6 mb-2 flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/25 rounded-xl animate-in slide-in-from-bottom-2 duration-200">
              <span className="text-xs font-mono text-primary/70 whitespace-nowrap flex-shrink-0">
                (reverse-i-search)`{reverseSearchQuery}':
              </span>
              <input
                ref={reverseSearchInputRef}
                type="text"
                value={reverseSearchQuery}
                onChange={(e) => { setReverseSearchQuery(e.target.value); setReverseSearchIndex(0); }}
                onKeyDown={handleReverseSearchKeyDown}
                className="flex-1 bg-transparent border-none outline-none text-sm text-foreground font-medium placeholder:text-foreground/40"
                placeholder="type to search history..."
              />
              <span className="text-xs text-foreground/40 whitespace-nowrap flex-shrink-0">
                {reverseSearchMatches.length > 0 ? `${reverseSearchIndex + 1}/${reverseSearchMatches.length}` : reverseSearchQuery ? 'no match' : ''}
              </span>
              <button type="button" onClick={() => closeReverseSearch(true)} className="text-foreground/40 hover:text-foreground text-xs ml-1" title="Cancel (Esc)">&times;</button>
            </div>
          )}
          <div className="border-t border-white/10 bg-gradient-to-t from-background to-background/98 backdrop-blur-xl">
            <div className="flex gap-3 items-end px-4 md:px-6 py-4">
              {/* History navigation arrows — always visible on mobile, hidden on desktop (keyboard arrows work) */}
              <div className="flex flex-col gap-1 flex-shrink-0 md:hidden">
                <button
                  type="button"
                  onClick={navigateHistoryUp}
                  disabled={messageHistory.length === 0}
                  className="p-1.5 rounded-lg border border-white/10 bg-white/5 text-foreground/50 hover:text-foreground hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  title="Previous message"
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  type="button"
                  onClick={navigateHistoryDown}
                  disabled={historyIndex === -1}
                  className="p-1.5 rounded-lg border border-white/10 bg-white/5 text-foreground/50 hover:text-foreground hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  title="Next message"
                >
                  <ChevronDown size={14} />
                </button>
              </div>
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about this task..."
                disabled={sendingTaskChat}
                rows={1}
                className="flex-1 bg-gradient-to-br from-white/8 to-white/4 border border-white/15 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-white/20 disabled:opacity-40 transition-all placeholder:text-foreground/50 font-medium backdrop-blur-sm"
              />
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setTaskAttachmentLibraryOpen(true)}
                  className="p-2.5 rounded-xl border border-white/10 bg-white/5 text-foreground/50 hover:text-foreground hover:bg-white/10 transition-all"
                  title="Attach file"
                >
                  <Paperclip size={16} />
                </button>
                {sendingTaskChat ? (
                  <Button
                    onClick={stopTaskChatGeneration}
                    variant="destructive"
                    size="sm"
                    title="Stop generation"
                    aria-label="Stop generation"
                    className="h-10 w-10 flex-shrink-0 rounded-xl"
                  >
                    <Square className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSend}
                    disabled={sendingTaskChat || !input.trim()}
                    variant="primary"
                    size="sm"
                    title="Send message"
                    aria-label="Send message"
                    className="h-10 w-10 flex-shrink-0 rounded-xl shadow-xl shadow-primary/25 hover:shadow-primary/40 hover:scale-105 transition-all duration-200"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
          </>
        )}
        </div>
        <TaskDetailModal
          taskId={focusedTaskId}
          open={manageTaskOpen}
          onOpenChange={(open) => {
            setManageTaskOpen(open);
            if (!open && onExternalManageClose) onExternalManageClose();
          }}
        />
        <ChatAttachmentsLibrary
          sessionId={taskAttachmentSessionId}
          isOpen={taskAttachmentLibraryOpen}
          onClose={() => setTaskAttachmentLibraryOpen(false)}
          onSelectAttachment={handleTaskAttachmentSelect}
        />
        {editingPdfUrl && PdfEditorComp && createPortal(
          <PdfEditorComp
            pdfUrl={editingPdfUrl}
            onClose={() => setEditingPdfUrl(null)}
            onSaved={() => {}}
          />,
          document.body,
        )}
      </>
    );
  }

  // ── Default chat view (no task focused) ──
  return (
    <div className="h-full w-full flex flex-col bg-background">
      {/* Premium gradient overlay header for depth */}
      <div className="h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

      {/* New Chat button - visible when there are messages */}
      {messages.length > 0 && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-border/30 bg-background/80 backdrop-blur-sm">
          <span className="text-xs text-foreground/50 font-medium">
            {messages.filter(m => m.role === 'user').length} message{messages.filter(m => m.role === 'user').length !== 1 ? 's' : ''}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              clearMessages();
              setInput('');
              textareaRef.current?.focus();
            }}
            className="text-xs gap-1.5 text-foreground/60 hover:text-foreground"
          >
            <RefreshCw className="w-3 h-3" />
            New Chat
          </Button>
        </div>
      )}

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-8 py-6 space-y-5 scroll-smooth"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'var(--scrollbar-thumb) var(--scrollbar-track)',
        }}
      >
        {messages.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center space-y-8 max-w-2xl">
              {/* Premium icon with glow */}
              <div className="relative inline-block mx-auto">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary via-primary/60 to-primary/30 blur-2xl opacity-20" />
                <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/20 text-primary">
                  <Zap className="w-10 h-10" strokeWidth={1.5} />
                </div>
              </div>

              <div className="space-y-3">
                <h2 className="text-2xl font-bold text-foreground tracking-tight">TaskingBot</h2>
                <p className="text-base text-foreground/65 leading-relaxed">
                  Autonomous task execution & intelligent workflow automation
                </p>
              </div>

              {/* Minimal hint */}
              <p className="text-xs text-foreground/40 pt-4">
                Type a task to get started. Auto-execution is {autoExecute ? 'on' : 'off'}.
              </p>
            </div>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex gap-4 animate-in fade-in-50 slide-in-from-bottom-4 duration-300 ${
                msg.role === 'user' ? 'justify-end' : 'justify-start'
              } ${msg.role === 'system' ? 'justify-center' : ''}`}
            >
              {msg.role === 'system' ? (
                // System message - participant join notification
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-accent/20 to-accent/10 border border-accent/30 shadow-sm">
                  <UserPlus className="w-3.5 h-3.5 text-accent flex-shrink-0" />
                  <span className="text-xs font-medium text-accent-foreground">{msg.content}</span>
                </div>
              ) : (
                <div
                  className={`rounded-2xl px-5 py-4 text-sm leading-relaxed transition-all min-w-0 overflow-hidden ${
                    msg.role === 'user'
                      ? 'max-w-[85%] bg-gradient-to-br from-primary via-primary/95 to-primary/85 text-primary-foreground shadow-xl shadow-primary/15 rounded-br-sm border border-primary/30'
                      : msg.content.startsWith('❌')
                        ? 'max-w-full bg-gradient-to-br from-destructive/12 to-destructive/8 border border-destructive/25 text-foreground rounded-bl-sm shadow-lg shadow-destructive/8 backdrop-blur-xs'
                        : 'max-w-full bg-gradient-to-br from-white/6 to-white/2 border border-white/12 text-foreground rounded-bl-sm shadow-xl backdrop-blur-xl'
                  }`}
                >
                  {msg.role !== 'user' ? (
                    <>
                      <RichMessageContent content={msg.content} onRegenerate={sendMessageText} />
                      <MessageFeedback
                        messageId={String(msg.id)}
                        source="taskingbot"
                        messageContent={msg.content}
                        onRetry={() => {
                          const prevUserMsg = messages.slice(0, messages.indexOf(msg)).reverse().find(m => m.role === 'user');
                          if (prevUserMsg) sendMessageText(prevUserMsg.content);
                        }}
                      />
                    </>
                  ) : (
                    <p className="whitespace-pre-wrap break-words font-medium tracking-tight">
                      {msg.content}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))
        )}
        {isLoading && (
          <div className="flex gap-4 animate-in fade-in-50 slide-in-from-bottom-4 duration-300">
            <div className="bg-gradient-to-br from-white/6 to-white/2 border border-white/12 rounded-2xl px-5 py-4 shadow-xl backdrop-blur-xl rounded-bl-sm flex items-center gap-3">
              {messages[messages.length - 1]?.role === 'user' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                  <span className="text-sm text-foreground/75 font-semibold">
                    Processing request...
                  </span>
                </>
              ) : (
                <span className="text-sm text-foreground/50 font-semibold tracking-widest animate-pulse">
                  ...
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Error banner - premium style */}
      {error && (
        <div className="mx-6 mb-4 px-5 py-3.5 bg-gradient-to-r from-destructive/12 to-destructive/8 border border-destructive/25 rounded-xl flex gap-3 items-start text-sm text-destructive animate-in slide-in-from-top-2 duration-300 backdrop-blur-xs shadow-lg shadow-destructive/5">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="flex-1 font-semibold">{error}</div>
        </div>
      )}

      {/* Ctrl+R reverse search bar */}
      {reverseSearchActive && (
        <div className="mx-6 mb-2 flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/25 rounded-xl animate-in slide-in-from-bottom-2 duration-200">
          <span className="text-xs font-mono text-primary/70 whitespace-nowrap flex-shrink-0">
            (reverse-i-search)`{reverseSearchQuery}':
          </span>
          <input
            ref={reverseSearchInputRef}
            type="text"
            value={reverseSearchQuery}
            onChange={(e) => {
              setReverseSearchQuery(e.target.value);
              setReverseSearchIndex(0);
            }}
            onKeyDown={handleReverseSearchKeyDown}
            className="flex-1 bg-transparent border-none outline-none text-sm text-foreground font-medium placeholder:text-foreground/40"
            placeholder="type to search history..."
          />
          <span className="text-xs text-foreground/40 whitespace-nowrap flex-shrink-0">
            {reverseSearchMatches.length > 0
              ? `${reverseSearchIndex + 1}/${reverseSearchMatches.length}`
              : reverseSearchQuery ? 'no match' : ''}
          </span>
          <button
            type="button"
            onClick={() => closeReverseSearch(true)}
            className="text-foreground/40 hover:text-foreground text-xs ml-1"
            title="Cancel (Esc)"
          >
            &times;
          </button>
        </div>
      )}

      {/* Premium input area with luxury styling */}
      <div className="border-t border-white/10 bg-gradient-to-t from-background to-background/98 backdrop-blur-xl">
        <div className="flex gap-3 items-end px-8 py-5">
          {/* Mobile history navigation arrows */}
          <div className="flex flex-col gap-1 flex-shrink-0 md:hidden">
            <button
              type="button"
              onClick={navigateHistoryUp}
              disabled={messageHistory.length === 0}
              className="p-1.5 rounded-lg border border-white/10 bg-white/5 text-foreground/50 hover:text-foreground hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              title="Previous message"
            >
              <ChevronUp size={14} />
            </button>
            <button
              type="button"
              onClick={navigateHistoryDown}
              disabled={historyIndex === -1}
              className="p-1.5 rounded-lg border border-white/10 bg-white/5 text-foreground/50 hover:text-foreground hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              title="Next message"
            >
              <ChevronDown size={14} />
            </button>
          </div>
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe your task..."
            disabled={isLoading}
            rows={1}
            className="flex-1 bg-gradient-to-br from-white/8 to-white/4 border border-white/15 rounded-xl px-5 py-3.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-white/20 disabled:opacity-40 transition-all placeholder:text-foreground/50 font-medium backdrop-blur-sm"
          />
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {searxToggleButton}
            <PersonaSelector sessionId="taskingbot-global" />
            <AiConfigDialog sessionId="taskingbot-global" />
            <Button
              onClick={handleSend}
              disabled={isLoading || !input.trim()}
              variant="primary"
              size="sm"
              isIcon
              title="Send message"
              aria-label="Send message"
              className="h-11 w-11 flex-shrink-0 rounded-xl shadow-xl shadow-primary/25 hover:shadow-primary/40 hover:scale-105 transition-all duration-200"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
